import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import useCalendarStore from '../../store/useCalendarStore';
import { 
    ChevronRight, FolderTree, FileText, Download, 
    Table2, Calculator, Image as ImageIcon, Search,
    Filter, BookOpen, Layers, Edit3, Trash2,
    GripVertical, Save, X, FolderPlus, FilePlus, PlusCircle, Plus, Check,
    Eye, EyeOff, HelpCircle, Layout, Library, Columns, Maximize2,
    Sigma, Table, Info, Highlighter, FileUp, Wand2,
    Database, ChevronDown, Settings2, Loader2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import DocumentProcessingPanel from './DocumentProcessingPanel';
import AIPdfParserPanel from './AIPdfParserPanel';
import { Sparkles } from 'lucide-react';
import { 
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import TurndownService from 'turndown';
import api from '../../api';

// ─── Embedding model options ──────────────────────────────────────────────────
const EMBEDDING_MODELS = [
    { id: 'text-embedding-3-small', label: 'text-embedding-3-small (default)', defaultTokens: 512 },
    { id: 'text-embedding-3-large', label: 'text-embedding-3-large', defaultTokens: 512 },
    { id: 'text-embedding-ada-002', label: 'text-embedding-ada-002 (legacy)', defaultTokens: 256 },
];

// ─── Table names in content schema ───────────────────────────────────────────
const T = {
    folders: 'knowledge_folders',
    files: 'knowledge_files',
};

const statusColors = {
    pending: "bg-amber-500/10 text-amber-600 border-amber-200/50",
    approved: "bg-emerald-500/10 text-emerald-600 border-emerald-200/50",
    rejected: "bg-red-500/10 text-red-600 border-red-200/50",
    in_review: "bg-blue-500/10 text-blue-600 border-blue-200/50",
};

const chunkTypeIcons = {
    theory: BookOpen,
    definition: Layers,
    example: ImageIcon,
    solved_problem: Calculator,
    question: HelpCircle,
    image: ImageIcon,
    table: Table2
};

const FolderTreeItem = ({ 
    folder, allFolders, allFiles, expandedFolders, setExpandedFolders, 
    selectedFolderId, onNavigate, onDelete,
    editingEntity, setEditingEntity, editValue, setEditValue, handleRename,
    activeFile, onFileClick,
    onMove 
}) => {
    const [isOver, setIsOver] = useState(false);
    const hasChildren = allFolders.some(f => f.parent_id === folder.id);
    const folderFiles = allFiles.filter(f => f.folder_id === folder.id);
    const isExpanded = expandedFolders[folder.id];
    const isSelected = selectedFolderId === folder.id;
    const children = allFolders.filter(f => f.parent_id === folder.id);
    const isEditing = editingEntity?.id === folder.id && (editingEntity?.type === 'folder' || editingEntity?.type === 'folders');

    const handleDragStart = (e, type, id) => {
        e.stopPropagation();
        e.dataTransfer.setData("application/json", JSON.stringify({ id, type }));
        e.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOver(false);
        try {
            const data = JSON.parse(e.dataTransfer.getData("application/json"));
            if (data.id === folder.id && data.type === 'folder') return;
            onMove(data.id, data.type, folder.id);
        } catch (err) {
            console.error("Drop failed", err);
        }
    };

    return (
        <div 
            className="select-none"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div 
                draggable
                onDragStart={(e) => handleDragStart(e, 'folder', folder.id)}
                onClick={(e) => {
                    if (isEditing) return;
                    e.stopPropagation();
                    onNavigate(folder);
                    setExpandedFolders(p => ({...p, [folder.id]: !p[folder.id]}));
                }}
                className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing text-sm font-medium transition-all group/folder",
                    isSelected 
                        ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20" 
                        : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-800",
                    isOver && !isSelected && "bg-blue-500/10 ring-2 ring-blue-500/50"
                )}
            >
                <ChevronRight className={cn(
                    "w-4 h-4 transition-transform shrink-0", 
                    !(hasChildren || folderFiles.length > 0) && "opacity-0",
                    isExpanded && "rotate-90"
                )} />
                {isEditing ? (
                    <input 
                        autoFocus
                        className="flex-1 bg-white/20 dark:bg-black/20 border-none outline-none text-sm px-1 rounded text-inherit"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={handleRename}
                        onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    <span className="truncate flex-1">{folder.name}</span>
                )}
                
                <div className="flex items-center gap-0.5 opacity-0 group-hover/folder:opacity-100 transition-opacity">
                    <button 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            setEditingEntity({ id: folder.id, type: 'folder' });
                            setEditValue(folder.name);
                        }}
                        className={cn(
                            "p-1 rounded hover:bg-white/20 transition-colors",
                            isSelected ? "text-blue-100" : "text-zinc-400"
                        )}
                    >
                        <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button 
                        onClick={(e) => { e.stopPropagation(); onDelete(folder.id, 'folder'); }}
                        className={cn(
                            "p-1 rounded hover:bg-red-500 hover:text-white transition-colors",
                            isSelected ? "text-blue-100" : "text-zinc-400"
                        )}
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
            {isExpanded && (hasChildren || folderFiles.length > 0) && (
                <div className="ml-4 mt-1 space-y-1 border-l border-zinc-200 dark:border-zinc-800 pl-2">
                    {children.map(child => (
                        <FolderTreeItem 
                            key={child.id}
                            folder={child}
                            allFolders={allFolders}
                            allFiles={allFiles}
                            expandedFolders={expandedFolders}
                            setExpandedFolders={setExpandedFolders}
                            selectedFolderId={selectedFolderId}
                            onNavigate={onNavigate}
                            onDelete={onDelete}
                            editingEntity={editingEntity}
                            setEditingEntity={setEditingEntity}
                            editValue={editValue}
                            setEditValue={setEditValue}
                            handleRename={handleRename}
                            activeFile={activeFile}
                            onFileClick={onFileClick}
                            onMove={onMove}
                        />
                    ))}
                    {folderFiles.map(file => {
                        const isFileEditing = editingEntity?.id === file.id && editingEntity?.type === 'file';
                        return (
                            <div 
                                key={file.id}
                                draggable
                                onDragStart={(e) => handleDragStart(e, 'file', file.id)}
                                onClick={(e) => {
                                    if (isFileEditing) return;
                                    e.stopPropagation();
                                    onFileClick(file);
                                }}
                                className={cn(
                                    "flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing text-sm transition-all group/file",
                                    activeFile?.id === file.id 
                                        ? "bg-zinc-200 dark:bg-zinc-800 text-blue-600 dark:text-blue-400 font-bold" 
                                        : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800/50"
                                )}
                            >
                                <FileText className="w-3.5 h-3.5 shrink-0" />
                                {isFileEditing ? (
                                    <input 
                                        autoFocus
                                        className="flex-1 bg-white/20 dark:bg-black/20 border-none outline-none text-xs px-1 rounded text-inherit"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onBlur={handleRename}
                                        onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                ) : (
                                    <span className="truncate flex-1 text-xs">{file.name}</span>
                                )}
                                <div className="flex items-center gap-0.5 opacity-0 group-hover/file:opacity-100 transition-opacity">
                                    <button 
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setEditingEntity({ id: file.id, type: 'file' });
                                            setEditValue(file.name);
                                        }}
                                        className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                                    >
                                        <Edit3 className="w-3 h-3 text-zinc-400" />
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); onDelete(file.id, 'file'); }}
                                        className="p-1 rounded hover:bg-red-500 hover:text-white transition-colors"
                                    >
                                        <Trash2 className="w-3 h-3 text-zinc-400" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};


export default function ContentExplorerPanel() {
    const [folders, setFolders] = useState([]); // This stores the full tree for sidebar
    const [allFiles, setAllFiles] = useState([]); // This stores all files for the sidebar tree
    const [loadingTree, setLoadingTree] = useState(false);
    const [chunks, setChunks] = useState([]); // This stores files for the current view
    const [subFolders, setSubFolders] = useState([]); // This stores folders for the current view
    const [loadingContent, setLoadingContent] = useState(false);
    
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [folderPath, setFolderPath] = useState([]); // Array of {id, name} for breadcrumbs
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedFolders, setExpandedFolders] = useState({});
    const [viewMode, setViewMode] = useState('split'); // 'edit', 'preview', 'split'
    const [editingEntity, setEditingEntity] = useState(null); // { id, type }
    const [editValue, setEditValue] = useState("");
    const [activeFile, setActiveFile] = useState(null);
    const [originalBody, setOriginalBody] = useState(""); 
    const [showSymbols, setShowSymbols] = useState(false);
    const [showMobileNav, setShowMobileNav] = useState(false);
    const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
    const [tableRows, setTableRows] = useState(3);
    const [tableCols, setTableCols] = useState(3);
    const scholarsanchorOpenedFiles = useCalendarStore(s => s.scholarsanchorOpenedFiles);
    const setScholarsAnchorOpenedFiles = useCalendarStore(s => s.setScholarsAnchorOpenedFiles);
    const [editorContent, setEditorContent] = useState("");
    const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
    const [isParserPanelOpen, setIsParserPanelOpen] = useState(false);
    const [isHighlightMode, setIsHighlightMode] = useState(false);
    const [isVisualMode, setIsVisualMode] = useState(true);
    const [isMainOver, setIsMainOver] = useState(false);
    const [mainOverId, setMainOverId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // ── Chunk Management State ──────────────────────────────────────────────
    const [isChunkDialogOpen, setIsChunkDialogOpen] = useState(false);
    const [chunkCourses, setChunkCourses] = useState([]);
    const [chunkSelectedCourseId, setChunkSelectedCourseId] = useState('');
    const [chunkSubjects, setChunkSubjects] = useState([]);
    const [chunkSubject, setChunkSubject] = useState('');
    const [showAdvancedChunk, setShowAdvancedChunk] = useState(false);
    const [chunkEmbeddingModel, setChunkEmbeddingModel] = useState(EMBEDDING_MODELS[0].id);
    const [chunkMaxTokens, setChunkMaxTokens] = useState(512);
    const [isChunking, setIsChunking] = useState(false);
    const visualRef = useRef(null);
    const editorRef = useRef(null);
    const previewRef = useRef(null);
    const isScrolling = useRef(null);
    const latestContent = useRef("");

    const turndownService = useMemo(() => {
        const service = new TurndownService({
            headingStyle: 'atx',
            hr: '---',
            bullet: '-',
            codeBlockStyle: 'fenced'
        });
        return service;
    }, []);

    useEffect(() => {
        if (activeFile) latestContent.current = activeFile.body;
    }, [activeFile?.body]);

    useEffect(() => {
        if (!activeFile) return;
        const timer = setInterval(() => {
            if (latestContent.current !== originalBody) {
                updateFileBody(activeFile.id, latestContent.current);
            }
        }, 30000); // 30 seconds
        return () => clearInterval(timer);
    }, [activeFile?.id, originalBody]);

    useEffect(() => {
        if (isVisualMode && visualRef.current && activeFile) {
            const html = marked.parse(activeFile.body);
            if (visualRef.current.innerHTML !== html && document.activeElement !== visualRef.current) {
                visualRef.current.innerHTML = html;
            }
        }
    }, [activeFile?.body, isVisualMode]);

    useEffect(() => {
        if (viewMode === 'split' || viewMode === 'preview') {
            setIsVisualMode(true);
        } else {
            setIsVisualMode(false);
        }
    }, [viewMode]);

    const handleEditorScroll = (e) => {
        if (viewMode !== 'split' || isScrolling.current === 'preview') return;
        isScrolling.current = 'editor';
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const ratio = scrollTop / (scrollHeight - clientHeight);
        if (previewRef.current) {
            previewRef.current.scrollTop = ratio * (previewRef.current.scrollHeight - previewRef.current.clientHeight);
        }
        setTimeout(() => isScrolling.current = null, 50);
    };

    const handlePreviewScroll = (e) => {
        if (viewMode !== 'split' || isScrolling.current === 'editor') return;
        isScrolling.current = 'preview';
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const ratio = scrollTop / (scrollHeight - clientHeight);
        if (editorRef.current) {
            editorRef.current.scrollTop = ratio * (editorRef.current.scrollHeight - editorRef.current.clientHeight);
        }
        setTimeout(() => isScrolling.current = null, 50);
    };

    const handleVisualInput = (e) => {
        const html = e.target.innerHTML;
        const markdown = turndownService.turndown(html);
        if (markdown !== activeFile.body) {
            setActiveFile({ ...activeFile, body: markdown });
        }
    };

    const mathSymbols = [
        { label: 'π', value: 'π' }, { label: 'Σ', value: 'Σ' }, { label: 'α', value: 'α' },
        { label: 'β', value: 'β' }, { label: 'γ', value: 'γ' }, { label: 'Δ', value: 'Δ' },
        { label: 'θ', value: 'θ' }, { label: 'λ', value: 'λ' }, { label: 'μ', value: 'μ' },
        { label: '∞', value: '∞' }, { label: '≈', value: '≈' }, { label: '≠', value: '≠' },
        { label: '±', value: '±' }, { label: '√', value: '√' }, { label: '∫', value: '∫' },
        { label: '∂', value: '∂' }, { label: '∇', value: '∇' }, { label: '∈', value: '∈' },
        { label: '⊆', value: '⊆' }, { label: '∀', value: '∀' }, { label: '∃', value: '∃' }
    ];

    const snippets = {
        table: "| Column 1 | Column 2 |\n| -------- | -------- |\n| Cell 1 | Cell 2 |",
        question: "\n> **Question:** \n> \n> **Answer:** \n",
        note: "\n> [!NOTE]\n> \n"
    };

    const insertSnippet = (snippet) => {
        const textarea = document.getElementById('markdown-editor');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = activeFile.body || "";
        const before = text.substring(0, start);
        const after = text.substring(end, text.length);

        const newContent = before + snippet + after;
        setActiveFile({ ...activeFile, body: newContent });
        
        // Return focus and set cursor
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + snippet.length, start + snippet.length);
        }, 10);
    };

    const handleInsertTable = () => {
        let md = "\n|";
        for (let c = 1; c <= tableCols; c++) md += ` Column ${c} |`;
        md += "\n|";
        for (let c = 1; c <= tableCols; c++) md += " --- |";
        md += "\n";
        for (let r = 1; r <= tableRows; r++) {
            md += "|";
            for (let c = 1; c <= tableCols; c++) md += ` Cell ${r}-${c} |`;
            md += "\n";
        }
        insertSnippet(md);
        setIsTableDialogOpen(false);
    };

    const toggleHighlight = () => {
        if (!activeFile) return;
        const textarea = document.getElementById('markdown-editor');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = activeFile.body || "";
        
        if (start !== end) {
            // Text is selected: wrap it and turn off highlight mode
            const selectedText = text.substring(start, end);
            const newSnippet = "`" + selectedText + "`";
            const before = text.substring(0, start);
            const after = text.substring(end, text.length);
            const newContent = before + newSnippet + after;
            setActiveFile({ ...activeFile, body: newContent });
            setIsHighlightMode(false); // Turn off glow when selection is processed
            
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start, start + newSnippet.length);
            }, 10);
        } else {
            // No selection: add one backtick and toggle glow
            const before = text.substring(0, start);
            const after = text.substring(end, text.length);
            const newContent = before + "`" + after;
            setActiveFile({ ...activeFile, body: newContent });
            setIsHighlightMode(!isHighlightMode);
            
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + 1, start + 1);
            }, 10);
        }
    };

    const syncFileMap = async () => {
        try {
            await api.post('/api/admin/file-map/sync');
        } catch (err) {
            console.error("Local sync failed:", err);
        }
    };


    // ─── Fetch Tree ──────────────────────────────────────────────────────────
    const fetchTree = async (silent = false) => {
        if (!silent) setLoadingTree(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            const teamId = profile.team_id;

            if (!teamId) {
                setLoadingTree(false);
                return;
            }

            // Fetch both folders and files in parallel (WITHOUT body for performance)
            const [foldersRes, filesRes] = await Promise.all([
                supabase.from(T.folders).select('id, name, parent_id, sort_order').eq('team_id', teamId).order('sort_order', { ascending: true }),
                supabase.from(T.files).select('id, name, folder_id, status').eq('team_id', teamId).order('name')
            ]);

            if (foldersRes.error) throw foldersRes.error;
            if (filesRes.error) throw filesRes.error;

            setFolders(foldersRes.data || []);
            setAllFiles(filesRes.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load explorer tree");
        } finally {
            if (!silent) setLoadingTree(false);
        }
    };

    useEffect(() => { 
        fetchTree();
        syncFileMap();
        fetchChunkCourses();
    }, []);

    const fetchChunkCourses = async () => {
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            if (!profile.team_id) return;
            const { data } = await supabase.from('courses').select('id, name').eq('team_id', profile.team_id).order('name');
            if (data) setChunkCourses(data);
        } catch (err) {
            console.error('Failed to fetch courses for chunking:', err);
        }
    };

    const fetchChunkSubjects = async (courseId) => {
        if (!courseId) {
            setChunkSubjects([]);
            return;
        }
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            if (!profile.team_id) return;
            const { data, error } = await supabase
                .from('subjects')
                .select('id, name')
                .eq('course_id', courseId)
                .eq('team_id', profile.team_id)
                .order('name');
            
            if (error) throw error;
            if (data) setChunkSubjects(data);
        } catch (err) {
            console.error('Failed to fetch subjects for course:', err);
        }
    };

    useEffect(() => {
        if (chunkSelectedCourseId) {
            fetchChunkSubjects(chunkSelectedCourseId);
        } else {
            setChunkSubjects([]);
        }
        setChunkSubject(''); // Reset subject when course changes
    }, [chunkSelectedCourseId]);

    const handleChunkFile = async () => {
        if (!activeFile) return;
        if (!chunkSelectedCourseId) return toast.error('Please select a course');
        if (!chunkSubject.trim()) return toast.error('Please enter a subject');
        setIsChunking(true);
        try {
            const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
            await api.post('/api/admin/content/chunk', {
                file_id: activeFile.id,
                team_id: profile.team_id,
                course_id: chunkSelectedCourseId,
                subject: chunkSubject.trim(),
                embedding_model: chunkEmbeddingModel,
                max_tokens: chunkMaxTokens,
            });
            toast.success('File chunked and added to database successfully!');
            setIsChunkDialogOpen(false);
            setChunkSubject('');
            setChunkSelectedCourseId('');
            setShowAdvancedChunk(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Chunking failed');
        } finally {
            setIsChunking(false);
        }
    };

    // ─── Fetch Main View Content (Using Local State for Speed) ──────────────────
    const fetchViewContent = () => {
        // This function now primarily filters the already-fetched 'folders' and 'allFiles' states
        // to avoid redundant database calls for simple navigation.
        
        if (selectedFolderId) {
            // Filter files and subfolders for this folder from local state
            const currentFiles = allFiles.filter(f => f.folder_id === selectedFolderId);
            const currentSubFolders = folders.filter(f => f.parent_id === selectedFolderId);
            
            setChunks(currentFiles);
            setSubFolders(currentSubFolders);
        } else {
            // Root View: Filter top-level items from local state
            const rootFiles = allFiles.filter(f => !f.folder_id);
            const rootFolders = folders.filter(f => !f.parent_id);
            
            setChunks(rootFiles);
            setSubFolders(rootFolders);
        }
    };

    // Re-run filter whenever the source data or selection changes
    useEffect(() => { 
        fetchViewContent(); 
    }, [selectedFolderId, folders, allFiles]);

    // ─── Creation Helpers ───────────────────────────────────────────────────
    const genId = (prefix) => `${prefix}_${Math.random().toString(36).substring(2, 9)}`;

    const createEntity = async (type) => {
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const teamId = profile.team_id;
        if (!teamId) return toast.error("No team session");

        let table, payload, successMsg;

        if (type === 'folder') {
            table = T.folders;
            payload = { 
                name: "New Folder", 
                team_id: teamId,
                parent_id: selectedFolderId,
                sort_order: (subFolders.length + 1)
            };
            successMsg = "Folder created";
        } else if (type === 'file') {
            if (!selectedFolderId) return toast.error("Select a folder first");
            table = T.files;
            payload = {
                folder_id: selectedFolderId,
                name: "New File",
                body: "# New Document\n\nStart writing...",
                team_id: teamId
            };
            successMsg = "File created";
        }

        let query = supabase.from(table);
        if (type === 'folder' || type === 'file') query = supabase.from(table);
        
        const { data, error } = await query.insert([payload]).select().single();
        if (error) return toast.error(error.message);

        toast.success(successMsg);
        fetchTree(true);
        fetchViewContent(true);
        setEditingEntity({ id: data.id, type });
        setEditValue(data.name);
        if (type === 'file') {
            setActiveFile(data);
            setOriginalBody(data.body || "");
        }
        syncFileMap();
    };

    // ─── Navigation & Path Helpers ───────────────────────────────────────────
    const calculatePath = (folderId, allFolders) => {
        const path = [];
        let currentId = folderId;
        while (currentId) {
            const folder = allFolders.find(f => f.id === currentId);
            if (folder) {
                path.unshift(folder);
                currentId = folder.parent_id;
            } else {
                break;
            }
        }
        return path;
    };

    const navigateToFolder = (folder) => {
        if (!folder) {
            setSelectedFolderId(null);
            setFolderPath([]);
            return;
        }
        setSelectedFolderId(folder.id);
        setFolderPath(calculatePath(folder.id, folders));
    };

    const handleFileClick = async (file) => {
        if (!scholarsanchorOpenedFiles.find(f => f.id === file.id)) {
            setScholarsAnchorOpenedFiles(prev => [...prev, file]);
        }

        // If the file content isn't loaded yet, fetch it now
        if (!file.body && file.id) {
            try {
                const { data, error } = await supabase
                    .from(T.files)
                    .select('body')
                    .eq('id', file.id)
                    .single();
                
                if (error) throw error;
                const fileWithBody = { ...file, body: data.body };
                
                setAllFiles(prev => prev.map(f => f.id === file.id ? fileWithBody : f));
                setScholarsAnchorOpenedFiles(prev => prev.map(f => f.id === file.id ? fileWithBody : f));
                setActiveFile(fileWithBody);
                setEditorContent(data.body || "");
                setOriginalBody(data.body || "");
            } catch (err) {
                console.error("Failed to load file content:", err);
                toast.error("Could not load file content");
            }
        } else {
            setActiveFile(file);
            setEditorContent(file.body || "");
            setOriginalBody(file.body || "");
        }
        setViewMode('edit');
    };

    const closeFile = (e, fileId) => {
        if (e) e.stopPropagation();
        const newOpenFiles = scholarsanchorOpenedFiles.filter(f => f.id !== fileId);
        setScholarsAnchorOpenedFiles(newOpenFiles);
        if (activeFile?.id === fileId) {
            const nextFile = newOpenFiles.length > 0 ? newOpenFiles[newOpenFiles.length - 1] : null;
            setActiveFile(nextFile);
            if (nextFile) {
                setEditorContent(nextFile.body || "");
                setOriginalBody(nextFile.body || "");
            }
        }
    };

    const requestNavigate = async (navigateFn) => {
        if (activeFile && activeFile.body !== originalBody) {
            const choice = window.confirm("You have unsaved changes. Click OK to Save and continue, or Cancel to Discard and continue.");
            if (choice) {
                await updateFileBody(activeFile.id, activeFile.body);
            }
        }
        setActiveFile(null);
        setOriginalBody("");
        navigateFn();
    };

    // ─── Navigation & Breadcrumbs ───────────────────────────────────────────
    const getBreadcrumbs = () => {
        const crumbs = [{ label: 'Root', onClick: () => navigateToFolder(null), active: !selectedFolderId }];
        folderPath.forEach((folder, idx) => {
            crumbs.push({
                label: folder.name,
                onClick: () => navigateToFolder(folder),
                active: idx === folderPath.length - 1
            });
        });
        return crumbs;
    };


    // ─── Rename & Delete ─────────────────────────────────────────────────────
    const handleRename = async () => {
        if (!editingEntity || !editValue.trim()) { setEditingEntity(null); return; }
        const { id, type } = editingEntity;
        const newName = editValue.trim();
        
        let table = T[type + 's'] || T[type];
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const { error } = await supabase.from(table).update({ name: newName }).eq('id', id).eq('team_id', profile.team_id);
        
        if (error) toast.error("Rename failed");
        else {
            toast.success("Renamed");
            fetchTree(true);
            fetchViewContent(true);
            syncFileMap();
        }
        setEditingEntity(null);
    };

    const handleDelete = async (id, type) => {
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const teamId = profile.team_id;

        // Smart delete for folders: check if empty
        if (type === 'folder') {
            const [fRes, subRes] = await Promise.all([
                supabase.from(T.files).select('id').eq('folder_id', id).eq('team_id', teamId).limit(1),
                supabase.from(T.folders).select('id').eq('parent_id', id).eq('team_id', teamId).limit(1)
            ]);
            
            const hasChildren = (fRes.data?.length > 0) || (subRes.data?.length > 0);
            if (hasChildren) {
                if (!window.confirm("This folder is not empty. Delete all contents?")) return;
            }
            // If empty, no confirmation needed as per user request
        } else {
            if (!window.confirm(`Delete this ${type}?`)) return;
        }

        const { error } = await supabase.from(T.files).delete().eq('id', id).eq('team_id', teamId);
        
        if (error) toast.error("Delete failed");
        else {
            toast.success("Deleted");
            if (id === selectedFolderId) {
                setSelectedFolderId(null);
                setFolderPath([]);
            }
            fetchTree(true);
            fetchViewContent(true);
            syncFileMap();
        }
    };

    const handleChunkContent = async () => {
        // Logic remains similar but uses the active selection
        const target = selectedFolderId;
        if (!target) return toast.error("Select a folder to index");
        
        toast.info("Indexing functionality currently limited to folder/subject level.");
        // (Implementation details for batch processing would go here)
    };

    const handleMove = async (id, type, newParentId) => {
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const teamId = profile.team_id;
        
        let table = T[type + 's'] || T[type];
        let column = type === 'folder' ? 'parent_id' : 'folder_id';
        
        // Prevent moving a folder into itself or its children
        if (type === 'folder') {
            if (id === newParentId) return;
            
            let currentId = newParentId;
            while (currentId) {
                const f = folders.find(folder => folder.id === currentId);
                if (f?.id === id) {
                    toast.error("Cannot move a folder into its own subfolder");
                    return;
                }
                currentId = f?.parent_id;
            }
        }

        // Optimistic UI Update: update local state immediately
        if (type === 'folder') {
            setFolders(prev => prev.map(f => f.id === id ? { ...f, parent_id: newParentId } : f));
        } else {
            setAllFiles(prev => prev.map(f => f.id === id ? { ...f, folder_id: newParentId } : f));
        }

        const { error } = await supabase
            .from(table)
            .update({ [column]: newParentId })
            .eq('id', id)
            .eq('team_id', teamId);
        
        if (error) {
            toast.error(`Move failed: ${error.message}`);
            // Rollback on error
            fetchTree(true);
        } else {
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} moved`);
            // We already updated state, but syncFileMap should be called
            syncFileMap();
        }
    };

    const handleDragStartMain = (e, type, id) => {
        e.dataTransfer.setData("application/json", JSON.stringify({ id, type }));
        e.dataTransfer.effectAllowed = "move";
    };

    const updateFileBody = async (id, body) => {
        if (!id) return false;
        
        const profile = JSON.parse(localStorage.getItem('admin_profile') || '{}');
        const teamId = profile.team_id;

        if (!teamId) {
            console.warn("Attempted to save file without a team_id context");
            toast.error("Session error: Team context missing.");
            return false;
        }

        try {
            const { error } = await supabase
                .from(T.files)
                .update({ 
                    body, 
                    updated_at: new Date().toISOString() 
                })
                .eq('id', id)
                .eq('team_id', teamId);

            if (error) {
                console.error("Supabase Save Error:", error);
                toast.error(`Failed to save: ${error.message}`);
                return false;
            } else {
                setOriginalBody(body);
                setAllFiles(prev => prev.map(f => f.id === id ? { ...f, body } : f));
                setScholarsAnchorOpenedFiles(prev => prev.map(f => f.id === id ? { ...f, body } : f));
                return true;
            }
        } catch (err) {
            console.error("Network/CORS Error during save:", err);
            toast.error("Connection error while saving. Please check for ad-blockers or VPNs.");
            return false;
        }
    };

    const handleManualSave = async () => {
        if (!activeFile) return;
        setIsSaving(true);
        try {
            await updateFileBody(activeFile.id, activeFile.body);
        } catch (err) {
            console.error("Manual save failed:", err);
        } finally {
            setIsSaving(false);
        }
    };




    return (
        <div className="flex flex-col h-full bg-white dark:bg-zinc-900 overflow-hidden">
            {/* ── Tabs Bar ────────────────────────────────────────────── */}
            <div className="flex items-center gap-1 px-4 bg-zinc-50 dark:bg-zinc-950/50 border-b border-zinc-200 dark:border-zinc-800 overflow-x-auto no-scrollbar pt-2 z-30">
                {/* Explorer Tab */}
                <div 
                    onClick={() => setActiveFile(null)}
                    className={cn(
                        "flex items-center gap-2.5 px-5 py-3 rounded-t-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer min-w-[120px] border-x border-t relative group",
                        !activeFile 
                            ? "bg-white dark:bg-zinc-950 text-blue-600 border-zinc-200 dark:border-zinc-800 shadow-[0_-4px_15px_rgba(0,0,0,0.05)] z-10" 
                            : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 border-transparent hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50"
                    )}
                >
                    <FolderTree className="w-3 h-3" />
                    <span className="truncate flex-1 font-black">Explorer</span>
                    {!activeFile && (
                        <div className="absolute bottom-[-1px] left-0 right-0 h-1 bg-white dark:bg-zinc-950 z-20" />
                    )}
                </div>

                {/* File Tabs */}
                {scholarsanchorOpenedFiles.map((file) => (
                    <div 
                        key={file.id}
                        onClick={() => {
                            setActiveFile(file);
                            setOriginalBody(file.body || "");
                            setEditorContent(file.body || "");
                        }}
                        className={cn(
                            "flex items-center gap-3 px-5 py-3 rounded-t-xl text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer min-w-[160px] max-w-[280px] border-x border-t relative group",
                            activeFile?.id === file.id 
                                ? "bg-white dark:bg-zinc-950 text-blue-600 border-zinc-200 dark:border-zinc-800 shadow-[0_-4px_15px_rgba(0,0,0,0.05)] z-10" 
                                : "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 border-transparent hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50"
                        )}
                    >
                        <div className={cn(
                            "w-2 h-2 rounded-full",
                            activeFile?.id === file.id ? "bg-blue-600" : "bg-zinc-300 dark:bg-zinc-700"
                        )} />
                        <span className="truncate flex-1 font-black">{file.name}</span>
                        <button 
                            onClick={(e) => closeFile(e, file.id)}
                            className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                        >
                            <X className="w-3 h-3" />
                        </button>
                        {activeFile?.id === file.id && (
                            <div className="absolute bottom-[-1px] left-0 right-0 h-1 bg-white dark:bg-zinc-950 z-20" />
                        )}
                    </div>
                ))}
            </div>

            {/* ── Header ────────────────────────────────────────────────── */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-4 md:px-6 py-3 border-b border-zinc-200 dark:border-zinc-800 gap-4 bg-white dark:bg-zinc-900 z-20">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button onClick={() => setShowMobileNav(!showMobileNav)} className="p-2 md:hidden rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400">
                        <Filter className="w-5 h-5" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-lg md:text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight truncate">Knowledge Explorer</h1>
                        <p className="text-[10px] text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wide">Root · Folder · File Hierarchy</p>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    <button
                        onClick={() => activeFile && setIsChunkDialogOpen(true)}
                        disabled={!activeFile}
                        className={cn(
                            "p-2.5 rounded-xl transition-all shadow-sm flex items-center gap-2 border border-zinc-200/50 dark:border-zinc-800/50",
                            activeFile 
                                ? "bg-violet-600 hover:bg-violet-700 text-white shadow-violet-500/20 hover:scale-105" 
                                : "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 cursor-not-allowed opacity-50"
                        )}
                        title={activeFile ? "Manage Chunks for active file" : "Open a file to manage chunks"}
                    >
                        <Database className="w-4 h-4" />
                        <span className="text-[10px] font-black uppercase tracking-widest hidden lg:inline">Manage Chunks</span>
                    </button>

                    <button 
                        onClick={() => setIsProcessModalOpen(true)}
                        className="p-2.5 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400 transition-all shadow-sm border border-zinc-200/50 dark:border-zinc-800/50"
                        title="Document Convert (Process to MD)"
                    >
                        <Wand2 className="w-5 h-5" />
                    </button>

                    <div className="relative group flex-1 sm:flex-none">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <input
                            type="text"
                            placeholder="Search repository..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 w-full sm:w-48 lg:w-64"
                        />
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
                            <button
                                className="p-2 text-zinc-500 hover:text-blue-500 hover:bg-white dark:hover:bg-zinc-700 rounded-lg transition-all"
                                onClick={() => createEntity('folder')}
                                title="Add Folder"
                            >
                                <FolderPlus className="w-5 h-5" />
                            </button>
                            {selectedFolderId && (
                                <button
                                    className="p-2 text-zinc-500 hover:text-blue-500 hover:bg-white dark:hover:bg-zinc-700 rounded-lg transition-all"
                                    onClick={() => createEntity('file')}
                                    title="Add File"
                                >
                                    <FilePlus className="w-5 h-5" />
                                </button>
                            )}
                        </div>

                        <button 
                            onClick={() => setIsParserPanelOpen(!isParserPanelOpen)}
                            className={cn(
                                "p-2.5 rounded-xl transition-all shadow-sm",
                                isParserPanelOpen 
                                    ? "bg-blue-600 text-white shadow-blue-500/30" 
                                    : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400"
                            )}
                            title="AI Content Parser"
                        >
                            <Sparkles className={cn("w-5 h-5", isParserPanelOpen && "animate-pulse")} />
                        </button>
                    </div>
                </div>
            </div>

                    <div className="flex flex-1 overflow-hidden relative">
                        {/* ── Sidebar Tree ────────────────────────────────────────── */}
                        <div className={cn(
                            "border-r border-zinc-200 dark:border-zinc-800 overflow-y-auto p-4 bg-zinc-50/50 dark:bg-zinc-950/30 shrink-0 transition-all duration-300 z-50",
                            "fixed md:relative inset-y-0 left-0 w-72 md:w-64 lg:w-72",
                            showMobileNav ? "translate-x-0" : "-translate-x-full md:translate-x-0"
                        )}>
                            <div className="flex items-center justify-between mb-6 px-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Folders</p>
                            </div>
                            {loadingTree && folders.length === 0 ? (
                                <div className="space-y-2 animate-pulse">
                                    {[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-zinc-100 dark:bg-zinc-800 rounded-lg" />)}
                                </div>
                            ) : (
                                <div 
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        try {
                                            const data = JSON.parse(e.dataTransfer.getData("application/json"));
                                            handleMove(data.id, data.type, null);
                                        } catch (err) {}
                                    }}
                                    className="space-y-1 min-h-[100px]"
                                >
                                    {folders.filter(f => !f.parent_id).map(rootFolder => (
                                        <FolderTreeItem 
                                            key={rootFolder.id}
                                            folder={rootFolder}
                                            allFolders={folders}
                                            allFiles={allFiles}
                                            expandedFolders={expandedFolders}
                                            setExpandedFolders={setExpandedFolders}
                                            selectedFolderId={selectedFolderId}
                                            onNavigate={(f) => {
                                                requestNavigate(() => {
                                                    navigateToFolder(f);
                                                    setShowMobileNav(false);
                                                });
                                            }}
                                            onDelete={handleDelete}
                                            editingEntity={editingEntity}
                                            setEditingEntity={setEditingEntity}
                                            editValue={editValue}
                                            setEditValue={setEditValue}
                                            handleRename={handleRename}
                                            activeFile={activeFile}
                                            onFileClick={handleFileClick}
                                            onMove={handleMove}
                                        />
                                    ))}
                                    {/* Root-level files */}
                                    {allFiles.filter(f => !f.folder_id).map(file => (
                                        <div 
                                            key={file.id}
                                            draggable
                                            onDragStart={(e) => handleDragStartMain(e, 'file', file.id)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleFileClick(file);
                                            }}
                                    className={cn(
                                        "flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-grab active:cursor-grabbing text-sm transition-all group/file",
                                        activeFile?.id === file.id 
                                            ? "bg-zinc-200 dark:bg-zinc-800 text-blue-600 dark:text-blue-400 font-bold" 
                                            : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800/50"
                                    )}
                                >
                                    <FileText className="w-3.5 h-3.5 shrink-0" />
                                    <span className="truncate flex-1 text-xs">{file.name}</span>
                                </div>
                            ))}
                            {folders.length === 0 && !loadingTree && (
                                <div className="px-2 py-8 text-center">
                                    <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">No folders yet</p>
                                </div>
                            )}
                        </div>

                    )}
                </div>

                {/* ── Main View ────────────────────────────────────────────── */}
                <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-zinc-950 overflow-hidden relative">
                    {!activeFile ? (
                        <>
                            {/* Breadcrumbs */}
                            <div className="px-6 py-3 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-zinc-400 bg-white dark:bg-zinc-950 z-10">
                                {getBreadcrumbs().map((crumb, idx) => (
                                    <React.Fragment key={idx}>
                                        {idx > 0 && <ChevronRight className="w-3 h-3" />}
                                        <button 
                                            onClick={() => requestNavigate(crumb.onClick)}
                                            disabled={crumb.active}
                                            className={cn("hover:text-blue-500 transition-colors", crumb.active && "text-zinc-900 dark:text-zinc-100")}
                                        >
                                            {crumb.label}
                                        </button>
                                    </React.Fragment>
                                ))}
                            </div>

                            <div 
                                className={cn(
                                    "flex-1 overflow-y-auto p-4 md:p-5 transition-colors duration-200",
                                    isMainOver && "bg-blue-500/5 ring-2 ring-inset ring-blue-500/20"
                                )}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsMainOver(true);
                                }}
                                onDragLeave={() => setIsMainOver(false)}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsMainOver(false);
                                    try {
                                        const data = JSON.parse(e.dataTransfer.getData("application/json"));
                                        handleMove(data.id, data.type, null);
                                    } catch (err) {}
                                }}
                            >
                                <div className="space-y-8">
                                    {/* Grid View for Folders/Subjects/Courses */}
                                    {subFolders.length > 0 && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                            {subFolders.map(item => {
                                                const isEditing = editingEntity?.id === item.id && editingEntity?.type === 'folder';
                                                return (
                                                    <div 
                                                        key={item.id}
                                                        draggable
                                                        onDragStart={(e) => handleDragStartMain(e, 'folder', item.id)}
                                                        onDragOver={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setMainOverId(item.id);
                                                        }}
                                                        onDragLeave={() => setMainOverId(null)}
                                                        onDrop={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setMainOverId(null);
                                                            try {
                                                                const data = JSON.parse(e.dataTransfer.getData("application/json"));
                                                                if (data.id === item.id && data.type === 'folder') return;
                                                                handleMove(data.id, data.type, item.id);
                                                            } catch (err) {}
                                                        }}
                                                        onClick={() => !isEditing && navigateToFolder(item)}
                                                        className={cn(
                                                            "group bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-[24px] p-6 cursor-grab active:cursor-grabbing hover:border-blue-500 hover:bg-white dark:hover:bg-zinc-900 transition-all duration-300",
                                                            mainOverId === item.id && "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                                        )}
                                                    >
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="p-3 rounded-2xl text-white shadow-lg bg-blue-600 shadow-blue-500/20">
                                                                <FolderPlus className="w-6 h-6" />
                                                            </div>
                                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button 
                                                                    onClick={(e) => { 
                                                                        e.stopPropagation(); 
                                                                        setEditingEntity({ id: item.id, type: 'folder' });
                                                                        setEditValue(item.name);
                                                                    }} 
                                                                    className="p-2 text-zinc-400 hover:text-blue-500 transition-colors"
                                                                >
                                                                    <Edit3 className="w-4 h-4" />
                                                                </button>
                                                                <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id, 'folder'); }} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {isEditing ? (
                                                            <input 
                                                                autoFocus
                                                                className="w-full bg-white/50 dark:bg-black/20 border border-blue-500 rounded-lg px-2 py-1 text-sm font-black uppercase tracking-tight outline-none"
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onBlur={handleRename}
                                                                onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        ) : (
                                                            <h4 className="text-sm font-black text-zinc-900 dark:text-zinc-100 uppercase tracking-tight line-clamp-1">{item.name}</h4>
                                                        )}
                                                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mt-1">
                                                            Folder
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* File List */}
                                    {chunks.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 border-b border-zinc-100 dark:border-zinc-800 pb-2">
                                                <FileText className="w-3 h-3" />
                                                Documents ({chunks.length})
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {chunks.map(file => {
                                                    const isFileEditing = editingEntity?.id === file.id && editingEntity?.type === 'file';
                                                    return (
                                                        <div 
                                                            key={file.id}
                                                            draggable
                                                            onDragStart={(e) => handleDragStartMain(e, 'file', file.id)}
                                                            onClick={() => !isFileEditing && handleFileClick(file)}
                                                            className="group bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl p-5 cursor-grab active:cursor-grabbing hover:border-blue-500 hover:shadow-xl transition-all"
                                                        >
                                                            <div className="flex items-center gap-3 mb-4">
                                                                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded-xl">
                                                                    <FileText className="w-5 h-5" />
                                                                </div>
                                                                {isFileEditing ? (
                                                                    <input 
                                                                        autoFocus
                                                                        className="flex-1 bg-zinc-50 dark:bg-zinc-800 border border-blue-500 rounded-lg px-2 py-1 text-xs font-bold outline-none"
                                                                        value={editValue}
                                                                        onChange={(e) => setEditValue(e.target.value)}
                                                                        onBlur={handleRename}
                                                                        onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                ) : (
                                                                    <h4 className="text-xs font-bold truncate flex-1">{file.name}</h4>
                                                                )}
                                                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <button 
                                                                        onClick={(e) => { 
                                                                            e.stopPropagation(); 
                                                                            setEditingEntity({ id: file.id, type: 'file' });
                                                                            setEditValue(file.name);
                                                                        }} 
                                                                        className="p-1 text-zinc-400 hover:text-blue-500 transition-colors"
                                                                    >
                                                                        <Edit3 className="w-4 h-4" />
                                                                    </button>
                                                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(file.id, 'file'); }} className="p-1 text-zinc-400 hover:text-red-500 transition-colors">
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-[10px] font-bold text-zinc-400 uppercase">{file.body?.length || 0} chars</span>
                                                                <span className={cn("px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-tighter border", 
                                                                    (file.status === 'success' || file.status === 'approved') ? "bg-emerald-500/10 text-emerald-600 border-emerald-200" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border-zinc-200")}>
                                                                    {file.status || 'idle'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {subFolders.length === 0 && chunks.length === 0 && (
                                        <div className="h-96 flex flex-col items-center justify-center text-center">
                                            <div className="w-20 h-20 bg-zinc-50 dark:bg-zinc-900 rounded-[32px] flex items-center justify-center mb-6">
                                                <Layers className="w-10 h-10 text-zinc-300" />
                                            </div>
                                            <h3 className="text-lg font-black text-zinc-900 dark:text-zinc-100 mb-2">This level is empty</h3>
                                            <p className="text-sm text-zinc-500 max-w-xs mx-auto mb-8">Start by adding a new sub-folder or document to populate this section.</p>
                                            <div className="flex flex-col sm:flex-row items-center gap-4">
                                                <button 
                                                    onClick={() => {
                                                        if (!selectedFolderId) {
                                                            toast.info("Files must be created inside folders. Create a folder first.");
                                                        } else {
                                                            createEntity('file');
                                                        }
                                                    }}
                                                    className={cn(
                                                        "px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl transition-all flex items-center gap-2",
                                                        selectedFolderId 
                                                            ? "bg-blue-600 text-white shadow-blue-500/30 hover:scale-105" 
                                                            : "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 cursor-not-allowed"
                                                    )}
                                                >
                                                    <FilePlus className="w-4 h-4" />
                                                    Add File
                                                </button>
                                                <button 
                                                    onClick={() => createEntity('folder')}
                                                    className="px-8 py-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl hover:scale-105 transition-all flex items-center gap-2"
                                                >
                                                    <FolderPlus className="w-4 h-4" />
                                                    Create Folder
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {/* Compact Combined Toolbar */}
                            <div className="px-6 py-2.5 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between bg-white dark:bg-zinc-950 gap-4 relative z-20 overflow-visible">
                                <div className="flex items-center gap-4 shrink-0">
                                    <h2 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 truncate max-w-[180px]">
                                        {activeFile.name}
                                    </h2>
                                    <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl">
                                        <button
                                            onClick={() => setIsVisualMode(true)}
                                            className={cn("px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all", isVisualMode ? "bg-white dark:bg-zinc-700 text-blue-600 shadow-sm" : "text-zinc-500 hover:text-zinc-700")}
                                        >
                                            Visual
                                        </button>
                                        <button
                                            onClick={() => setIsVisualMode(false)}
                                            className={cn("px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all", !isVisualMode ? "bg-white dark:bg-zinc-700 text-blue-600 shadow-sm" : "text-zinc-500 hover:text-zinc-700")}
                                        >
                                            Source
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5 flex-1 justify-center min-w-max">
                                    <button
                                        onClick={toggleHighlight}
                                        className={cn(
                                            "p-2 rounded-lg transition-all",
                                            isHighlightMode 
                                                ? "bg-amber-500 text-white shadow-lg shadow-amber-500/30 animate-pulse" 
                                                : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                        )}
                                        title="Highlight Mode (`)"
                                    >
                                        <Highlighter className="w-4 h-4" />
                                    </button>
                                    
                                    <div className="relative">
                                        <button
                                            onClick={() => setIsTableDialogOpen(!isTableDialogOpen)}
                                            className={cn(
                                                "p-2 rounded-lg transition-all",
                                                isTableDialogOpen ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600" : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                            )}
                                            title="Insert Table"
                                        >
                                            <Table className="w-4 h-4" />
                                        </button>

                                        {isTableDialogOpen && (
                                            <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl z-[100] p-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                                <div className="space-y-4">
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Rows</Label>
                                                        <Input
                                                            type="number"
                                                            value={tableRows}
                                                            onChange={(e) => setTableRows(parseInt(e.target.value))}
                                                            className="h-8 text-xs rounded-lg bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                                                        />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Columns</Label>
                                                        <Input
                                                            type="number"
                                                            value={tableCols}
                                                            onChange={(e) => setTableCols(parseInt(e.target.value))}
                                                            className="h-8 text-xs rounded-lg bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                                                        />
                                                    </div>
                                                    <Button 
                                                        onClick={handleInsertTable}
                                                        className="w-full h-8 text-[10px] font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                                                    >
                                                        Insert Table
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="w-[1px] h-4 bg-zinc-200 dark:bg-zinc-800 mx-1" />

                                    <button
                                        onClick={() => insertSnippet(snippets.question)}
                                        className="px-2 py-1 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-all"
                                    >
                                        Question
                                    </button>
                                    <button
                                        onClick={() => insertSnippet(snippets.note)}
                                        className="px-2 py-1 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-all"
                                    >
                                        Note
                                    </button>

                                    <div className="w-[1px] h-4 bg-zinc-200 dark:bg-zinc-800 mx-1" />

                                    <button
                                        onClick={() => setShowSymbols(!showSymbols)}
                                        className={cn(
                                            "p-2 rounded-lg transition-all",
                                            showSymbols ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600" : "text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                        )}
                                    >
                                        <Sigma className="w-4 h-4" />
                                    </button>

                                    {showSymbols && (
                                        <div className="flex items-center gap-0.5 ml-2 bg-zinc-50 dark:bg-zinc-900 p-0.5 rounded-lg border border-zinc-200 dark:border-zinc-800 animate-in fade-in zoom-in duration-200 max-w-[200px] overflow-x-auto no-scrollbar">
                                            {mathSymbols.map((sym, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => insertSnippet(sym.value)}
                                                    className="w-7 h-7 shrink-0 flex items-center justify-center text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800 rounded transition-all"
                                                >
                                                    {sym.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                    {isSaving ? (
                                        <button
                                            disabled
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-600/80 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-500/20 cursor-not-allowed opacity-80"
                                        >
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            Saving...
                                        </button>
                                    ) : activeFile.body === originalBody ? (
                                        <button
                                            disabled
                                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-500 shadow-lg shadow-emerald-500/20 cursor-default animate-in fade-in zoom-in-95 duration-300"
                                        >
                                            <Check className="w-3.5 h-3.5 transition-transform scale-110 duration-500" />
                                            Saved
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleManualSave}
                                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg shadow-blue-500/20 hover:scale-[1.02] active:scale-[0.98]"
                                        >
                                            <Save className="w-3.5 h-3.5 animate-pulse" />
                                            Save Changes
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => closeFile(e, activeFile.id)}
                                        className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Editor Content */}
                            <div className="flex-1 overflow-y-auto p-8 bg-zinc-50/30 dark:bg-zinc-950/20">
                                <div className="max-w-4xl mx-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-[32px] shadow-sm min-h-full p-12">
                                    {isVisualMode ? (
                                        <div
                                            ref={visualRef}
                                            contentEditable={true}
                                            onInput={handleVisualInput}
                                            className="outline-none min-h-full prose dark:prose-invert max-w-none"
                                            style={{ whiteSpace: 'pre-wrap' }}
                                        />
                                    ) : (
                                        <textarea
                                            id="markdown-editor"
                                            className="w-full h-full min-h-[500px] bg-transparent outline-none font-mono text-sm leading-relaxed border-none resize-none"
                                            value={activeFile.body}
                                            onChange={(e) => setActiveFile({ ...activeFile, body: e.target.value })}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                            </div>
                        {isParserPanelOpen && (
                            <div className="w-96 border-l border-zinc-200 dark:border-zinc-800 bg-zinc-50/30 dark:bg-zinc-950/20 flex flex-col animate-in slide-in-from-right duration-300">
                                <AIPdfParserPanel onClose={() => setIsParserPanelOpen(false)} />
                            </div>
                        )}
                    </div>
            {/* ── Manage Chunks Dialog ──────────────────────────────────── */}
            <Dialog open={isChunkDialogOpen} onOpenChange={setIsChunkDialogOpen}>
                <DialogContent className="sm:max-w-[520px] bg-white dark:bg-zinc-900 rounded-[24px] border border-zinc-200 dark:border-zinc-800 p-0 overflow-hidden">
                    <div className="px-6 pt-6 pb-4 border-b border-zinc-100 dark:border-zinc-800 bg-gradient-to-br from-violet-50 to-white dark:from-violet-900/20 dark:to-zinc-900">
                        <div className="flex items-center gap-3 mb-1">
                            <div className="p-2.5 rounded-xl bg-violet-600 text-white shadow-lg shadow-violet-500/30">
                                <Database className="w-5 h-5" />
                            </div>
                            <div>
                                <DialogTitle className="text-lg font-black text-zinc-900 dark:text-zinc-100 tracking-tight">Chunk &amp; Add to Database</DialogTitle>
                                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mt-0.5">File: {activeFile?.name}</p>
                            </div>
                        </div>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-3 leading-relaxed">
                            This will create semantic chunks of your content file and embed them into the knowledge database for AI-powered retrieval.
                        </p>
                    </div>

                    <div className="px-6 py-5 space-y-4">
                        {/* Course Selector */}
                        <div className="space-y-1.5">
                            <Label className="text-xs font-black uppercase tracking-widest text-zinc-500">Course</Label>
                            <select
                                value={chunkSelectedCourseId}
                                onChange={e => setChunkSelectedCourseId(e.target.value)}
                                className="w-full h-10 px-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-violet-500 outline-none transition-all"
                            >
                                <option value="">Select a course...</option>
                                {chunkCourses.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Subject Selector */}
                        <div className="space-y-1.5">
                            <Label className="text-xs font-black uppercase tracking-widest text-zinc-500">Subject</Label>
                            <select
                                value={chunkSubject}
                                onChange={e => setChunkSubject(e.target.value)}
                                disabled={!chunkSelectedCourseId}
                                className="w-full h-10 px-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-violet-500 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="">{chunkSelectedCourseId ? "Select a subject..." : "Select a course first"}</option>
                                {chunkSubjects.map(s => (
                                    <option key={s.id} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Advanced Settings Toggle */}
                        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden">
                            <button
                                onClick={() => setShowAdvancedChunk(!showAdvancedChunk)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <Settings2 className="w-4 h-4 text-zinc-400" />
                                    <span className="text-xs font-black uppercase tracking-widest text-zinc-500">Advanced Settings</span>
                                </div>
                                <ChevronDown className={cn("w-4 h-4 text-zinc-400 transition-transform", showAdvancedChunk && "rotate-180")} />
                            </button>
                            {showAdvancedChunk && (
                                <div className="px-4 py-4 space-y-4 bg-white dark:bg-zinc-900">
                                    {/* Embedding Model */}
                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-black uppercase tracking-widest text-zinc-500">Embedding Model</Label>
                                        <select
                                            value={chunkEmbeddingModel}
                                            onChange={e => {
                                                setChunkEmbeddingModel(e.target.value);
                                                const m = EMBEDDING_MODELS.find(m => m.id === e.target.value);
                                                if (m) setChunkMaxTokens(m.defaultTokens);
                                            }}
                                            className="w-full h-10 px-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl text-sm text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-violet-500 outline-none transition-all"
                                        >
                                            {EMBEDDING_MODELS.map(m => (
                                                <option key={m.id} value={m.id}>{m.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {/* Token Count */}
                                    <div className="space-y-1.5">
                                        <Label className="text-xs font-black uppercase tracking-widest text-zinc-500">Max Tokens per Chunk</Label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="range"
                                                min={128}
                                                max={1024}
                                                step={64}
                                                value={chunkMaxTokens}
                                                onChange={e => setChunkMaxTokens(Number(e.target.value))}
                                                className="flex-1 accent-violet-600"
                                            />
                                            <span className="text-sm font-black text-violet-600 w-12 text-right">{chunkMaxTokens}</span>
                                        </div>
                                        <p className="text-[10px] text-zinc-400">Higher = fewer larger chunks. Lower = more granular retrieval.</p>
                                    </div>
                                </div>
                            )}
                            {!showAdvancedChunk && (
                                <div className="px-4 py-2.5 bg-white dark:bg-zinc-900 flex items-center gap-2">
                                    <span className="text-[10px] text-zinc-400 font-medium">Using defaults: <span className="font-bold text-zinc-500">text-embedding-3-small · 512 tokens</span></span>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="px-6 pb-6 flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setIsChunkDialogOpen(false)} className="rounded-xl">Cancel</Button>
                        <Button
                            onClick={handleChunkFile}
                            disabled={isChunking}
                            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl gap-2 shadow-lg shadow-violet-500/20"
                        >
                            {isChunking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                            {isChunking ? 'Chunking...' : 'Add to Database'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isProcessModalOpen} onOpenChange={setIsProcessModalOpen}>
                <DialogContent className="max-w-4xl h-[80vh] p-0 overflow-hidden bg-white dark:bg-zinc-900 border-none rounded-[32px]">
                    <div className="flex flex-col h-full">
                        <div className="px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between bg-zinc-50/50 dark:bg-zinc-950/20">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                                    <Layout className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">Process Documents</h3>
                                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-widest">Targeting: {folderPath[folderPath.length-1]?.name || 'Current Folder'}</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => setIsProcessModalOpen(false)}
                                className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-400 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <DocumentProcessingPanel 
                                targetFolderId={selectedFolderId}
                                onSucceed={() => {
                                    setIsProcessModalOpen(false);
                                    fetchViewContent();
                                }}
                            />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Table Configuration Dialog ────────────────────────────── */}
        </div>
    );
}
